import { createFileRoute, retainSearchParams, useNavigate, useSearch } from "@tanstack/react-router"
import { fallback, zodValidator } from "@tanstack/zod-adapter"
import { useEffect, useState } from "react"
import { z } from "zod"

import { SectionContainer } from "@/components/SectionContainer/SectionContainer"
import { OrderListFilterSchema, OrderStatus, OrderType, SortingOrder } from "@/graphql/codegen/graphql"
import { PurchaseOrderFileDialog } from "@/screens/Procurement/PurchaseOrder/components/dialogs/PurchaseOrderFileDialog"
import { usePOFileUploadAction } from "@/screens/Procurement/PurchaseOrder/hooks/useHeaderActions/usePOFileUploadAction"
import { PurchaseOrderFilterDrawer } from "@/screens/Procurement/PurchaseOrders/components/filters/PurchaseOrderFilterDrawer"
import { PurchaseOrderSearchFilterChips } from "@/screens/Procurement/PurchaseOrders/components/filters/PurchaseOrderSearchFilterChips"
import { PurchaseOrdersSearchBar } from "@/screens/Procurement/PurchaseOrders/components/PurchaseOrdersSearchBar"
import { PurchaseOrdersTable } from "@/screens/Procurement/PurchaseOrders/components/PurchaseOrdersTable"
import { PurchaseProductOrdersTable } from "@/screens/Procurement/PurchaseOrders/components/PurchaseProductOrdersTable"
import { purcheseOrdersRouteDataPreload } from "@/screens/Procurement/PurchaseOrders/purcheseOrdersRouteDataPreload"
import { PurchaseOrderSearchCategory } from "@/screens/Procurement/types/enums"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"

export const routePath = "/app/procurement/_layout/purchase-orders"
const DEFAULT_STATUSES = [OrderStatus.Confirmed, OrderStatus.Draft, OrderStatus.Sent]

const searchSchema = z.object({
  ...OrderListFilterSchema().shape,
  category: fallback(z.nativeEnum(PurchaseOrderSearchCategory), PurchaseOrderSearchCategory.SUPPLIER),
  order: fallback(z.nativeEnum(SortingOrder), SortingOrder.Desc),
  query: fallback(z.string().optional().default(""), ""),
  start: z.string().optional(),
  end: z.string().optional(),
  outstandingTestResults: z.boolean().optional(),
})

export const Route = createFileRoute(routePath)({
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [retainSearchParams(["category", "query"])],
  },
  loader: purcheseOrdersRouteDataPreload,
  component: PurchaseOrdersComponent,
})

function PurchaseOrdersComponent() {
  const [orderId, setOrderId] = useState<string | null>(null)
  const [orderType, setOrderType] = useState<OrderType | undefined>(undefined)
  const [showFilter, setShowFilter] = useState(false)
  const navigate = useNavigate({ from: routePath })
  const currentSearch = useSearch({ from: routePath })

  const { dialogOpen: fileUploadModalOpen, setDialogOpen: setFileUploadModalOpen, canUpload } = usePOFileUploadAction()

  useEffect(() => {
    const params = getRefinedSearchParams(currentSearch, {
      ...currentSearch,
      statuses: DEFAULT_STATUSES,
    })
    void navigate(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUploadClick = (orderId: string, orderType?: OrderType) => {
    setOrderId(orderId)
    setOrderType(orderType)
    setFileUploadModalOpen(true)
  }

  const handleCloseFileUpload = () => {
    setFileUploadModalOpen(false)
    setOrderId(null)
    setOrderType(undefined)
  }

  return (
    <>
      {/* Filter Drawer */}
      <PurchaseOrderFilterDrawer
        routePath={routePath}
        showFilterDrawer={showFilter}
        toggleDrawer={() => setShowFilter(!showFilter)}
      />

      <SectionContainer className='flex flex-col gap-4 overflow-hidden'>
        {/* Search Bar */}
        <PurchaseOrdersSearchBar onShowFilter={() => setShowFilter(!showFilter)} />
        {/* Filter Chips */}
        <PurchaseOrderSearchFilterChips />
        {/* Table */}
        {currentSearch.category === PurchaseOrderSearchCategory.SUPPLIER ? (
          <PurchaseOrdersTable onUploadClick={handleUploadClick} canUpload={canUpload} />
        ) : (
          <PurchaseProductOrdersTable onUploadClick={handleUploadClick} canUpload={canUpload} />
        )}
      </SectionContainer>

      {fileUploadModalOpen && orderId && (
        <PurchaseOrderFileDialog
          open={fileUploadModalOpen}
          onClose={handleCloseFileUpload}
          onSuccess={handleCloseFileUpload}
          orderId={orderId}
          isQuote={orderType === OrderType.Quote}
        />
      )}
    </>
  )
}
